import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useState, useCallback, type ReactNode } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import { useStatsigDynamicHolidayConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-holiday-config.hook'
import { ExpressPay } from '@src/modules/express-pay'
import { useIsExpressPayEnabled } from '@src/modules/express-pay/express-pay'
import CheckIcon from '@src/quince-components/src/atoms/select/icons/check_circle.svg'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import Product from '@src/quince-site-common/src/domains/Product'
import productHelper, {
  CUSTOM_FIELD_NAMES,
  getFormattedCustomFieldText,
} from '@src/quince-site-common/src/helpers/productHelper'
import { useCart } from '@src/quince-site-common/src/hooks/cart'
import useATCVsATBExperiment from '@src/quince-site-common/src/hooks/use-atc-vs-atb-experiment'
import { useResponsiveAction } from '@src/quince-site-common/src/hooks/use-responsive-action'
import { useWaitlistSmsModal } from '@src/quince-site-common/src/hooks/use-waitlist-sms-modal'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import validateEmail from '@src/quince-site-common/src/utils/validateEmail'
import { SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF } from '@src/quince-site-pdp/src/organisms/productPageSections/similarProducts/constant'

import { BUTTON_TYPES } from '../button'
import { DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS, EDDMessageContainer } from '../eddMessageContainer'
import QuinceIcon from '../icon'
import { transformImageSrc } from '../image/image.utils'
import ErrorStateIcon from '../productForm/icons/errorState.inline.svg'
import productFormStyles from '../productForm/productForm.module.scss'
import { PURCHASE_TYPES } from '../purchaseTypeSelector/PurchaseTypeSelector'
import WaitlistSmsModal from '../waitlist-sms-modal'

import { ProductCTAConstants } from './constants'
import { ProductDisplayButton } from './product-display-button'
import { ProductWaitlistCTA } from './product-waitlist-cta'
import styles from './productCTA.module.scss'
import { CTA_TYPE } from './productCTAConstants'
import { ProductCTAType, RenderErrorMessageType } from './types'
import { useGoldBarProductEligibility } from './useGoldBarProductEligibility'
import { getIsGiftCard } from './utils'

export function getTrackingObject(key: string, selectedVariantId?: number | undefined) {
  return {
    action: key,
    value: true,
    context: {
      productId: selectedVariantId,
    },
  }
}

export const RenderErrorMessage = ({
  errorMessage,
  className,
  isQuickATCForMobile,
  showNewPdpDesignV2,
  hideIcon,
  isFromCartPopup,
}: RenderErrorMessageType) => {
  return (
    <div
      className={classNames(
        styles.errorMessage,
        className,
        isQuickATCForMobile && styles.errorMessage__quickATC,
        showNewPdpDesignV2 && styles.errorMessage_newDesignV2,
        showNewPdpDesignV2 && isFromCartPopup && styles.errorMessage_textAlignment,
      )}
    >
      {showNewPdpDesignV2 && !hideIcon ? <QuinceIcon iconSvg={ErrorStateIcon} className={styles.errorIcon} /> : null}
      {errorMessage}
    </div>
  )
}

const NonPhysicalProductDeliveryText = ({
  inStockMsgClass,
  isQuickATCForMobile,
  isFromCartPopup,
}: {
  inStockMsgClass?: string
  isQuickATCForMobile?: boolean
  isFromCartPopup?: boolean
}) => {
  const { t } = useTranslation('common')
  return (
    <div
      className={classNames(
        inStockMsgClass,
        styles.inStockAndReadyToShipMessage,
        isQuickATCForMobile && styles.inStockAndReadyToShipMessage__quickATC,
        styles.inStockAndReadyToShipMessage_adjustedPadding,
        isFromCartPopup && styles.inStockAndReadyToShipMessage_textAlignment,
      )}
    >
      {t('gift_card_delivery_info')}
    </div>
  )
}

export const HolidayMessage = ({
  inStockMsgClass,
  isQuickATCForMobile,
  showNewPdpDesignV2,
  eddDetail,
}: {
  inStockMsgClass?: string
  isQuickATCForMobile?: boolean
  showNewPdpDesignV2?: boolean
  eddDetail?: { displayTags?: string[] }
}) => {
  const { locale } = useLocaleContext()
  const { deviceType } = useDeviceInfo()
  const holidayConfig = useStatsigDynamicHolidayConfig()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE

  // Get all matching holiday messages with config (icon, textColor) if displayTags exists and matches config
  const holidayMessages: { message: string; icon?: string; textColor?: string }[] =
    eddDetail?.displayTags &&
    Array.isArray(eddDetail.displayTags) &&
    eddDetail.displayTags.length > 0 &&
    holidayConfig?.tags
      ? eddDetail.displayTags.flatMap(displayTag => {
          const tagConfig = holidayConfig.tags.find(tag => tag.tag === displayTag && tag.enabled)
          if (tagConfig?.message) {
            const message = tagConfig.message[locale] ?? Object.values(tagConfig.message)[0] ?? null
            if (message) {
              return [
                {
                  message,
                  icon: tagConfig.icon,
                  textColor: tagConfig.textColor,
                },
              ]
            }
          }
          return []
        })
      : []

  if (holidayMessages.length === 0) {
    return null
  }

  return (
    <>
      {holidayMessages.map((holidayMsg, index) => (
        <div
          key={index}
          className={classNames(
            inStockMsgClass,
            styles.inStockAndReadyToShipMessage,
            isQuickATCForMobile && styles.inStockAndReadyToShipMessage__quickATC,
            styles.inStockAndReadyToShipMessage_adjustedPadding,
            showNewPdpDesignV2 && styles.inStockAndReadyToShipMessage_newDesignV2,
          )}
        >
          <div className={styles.inStockAndReadyToShipMessage__holidayMessage} style={{ color: holidayMsg.textColor }}>
            {isMobile && holidayMsg.icon && (
              <img
                src={transformImageSrc(holidayMsg.icon)}
                alt=""
                className={styles.inStockAndReadyToShipMessage__holidayIcon}
              />
            )}
            <span>{holidayMsg.message}</span>
          </div>
        </div>
      ))}
    </>
  )
}

export const InStockAndReadyToShip = ({
  inStockMsgClass,
  isQuickATCForMobile,
  showNewPdpDesignV2,
  isFromCartPopup,
}: {
  inStockMsgClass?: string
  isQuickATCForMobile?: boolean
  showNewPdpDesignV2?: boolean
  isBuyNowPdp?: boolean
  isFromCartPopup?: boolean
}) => {
  const { t } = useTranslation('common')
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE

  const defaultMessage = t('in_stock_and_ready_to_ship')

  return (
    <div
      className={classNames(
        inStockMsgClass,
        styles.inStockAndReadyToShipMessage,
        isQuickATCForMobile && styles.inStockAndReadyToShipMessage__quickATC,
        styles.inStockAndReadyToShipMessage_adjustedPadding,
        showNewPdpDesignV2 && styles.inStockAndReadyToShipMessage_newDesignV2,
        showNewPdpDesignV2 && isFromCartPopup && styles.inStockAndReadyToShipMessage_textAlignment,
      )}
    >
      <div className={styles.inStockAndReadyToShipMessage__content}>
        {isMobile && <QuinceIcon iconSvg={CheckIcon} className={styles.inStockAndReadyToShipMessage__icon} />}
        <span>{defaultMessage}</span>
      </div>
    </div>
  )
}

const getIsProductExcludedForReadyToShip = (product: Product | undefined) => {
  // const productsExcludedForReadyToShipMessage: number[] = []  commented since it is unnecessary without any product ids added to it
  return !product
}

// Helper function to get base messaging conditions
export const getBaseMessagingConditions = (
  selectedVariantPurchaseInfo: ProductCTAType['selectedVariantPurchaseInfo'],
  showVariantAvailableInventory: boolean | undefined,
  product: Product | undefined,
  areAllVariantOOS: boolean,
  isGoldBarError: boolean,
  eddExpInit: boolean,
): boolean => {
  const isNonPhysicalProduct = product
    ? productHelper.getIsTruthyCustomField(product, CUSTOM_FIELD_NAMES.IS_NON_PHYSICAL_PRODUCT)
    : false
  const isMadeToOrderText = product
    ? getFormattedCustomFieldText(
        productHelper.getCustomFieldValue(product, CUSTOM_FIELD_NAMES.MADE_TO_ORDER_TEXT) ?? '',
      )
    : false
  const isMadeToOrder = product ? productHelper.getIsTruthyCustomField(product, CUSTOM_FIELD_NAMES.IS_MTO) : false
  const isPreorder = selectedVariantPurchaseInfo?.isPreorder
  const isExcludedForReadyToShipMessage = getIsProductExcludedForReadyToShip(product)
  return !!(
    selectedVariantPurchaseInfo?.isAnySelectedVariantInStock &&
    !showVariantAvailableInventory &&
    !isPreorder &&
    !selectedVariantPurchaseInfo?.showMadeToOrder &&
    !isMadeToOrderText &&
    !isMadeToOrder &&
    !isExcludedForReadyToShipMessage &&
    !areAllVariantOOS &&
    !isGoldBarError &&
    !isNonPhysicalProduct &&
    eddExpInit
  )
}

// Helper function to check if displayTags are valid and holiday config exists
const hasValidDisplayTags = (
  displayTags: string[] | undefined,
  holidayConfig: ReturnType<typeof useStatsigDynamicHolidayConfig> | null,
): boolean => {
  return !!(displayTags && Array.isArray(displayTags) && displayTags.length > 0 && holidayConfig?.tags)
}

// Helper function to check if any displayTag matches an enabled tag in holiday config
const checkHolidayMessageShown = (
  displayTags: string[],
  holidayConfig: ReturnType<typeof useStatsigDynamicHolidayConfig> | null,
  locale: string,
): boolean => {
  return displayTags.some(displayTag => {
    const tagConfig = holidayConfig?.tags?.find(tag => tag.tag === displayTag && tag.enabled)
    if (tagConfig?.message) {
      const message = tagConfig.message[locale] ?? Object.values(tagConfig.message)[0] ?? null
      return !!message
    }
    return false
  })
}

const ProductCTA = (props: ProductCTAType) => {
  const {
    selectedVariantPurchaseInfo,
    onJoinWaitList,
    type,
    showVariantAvailableInventory,
    product,
    inStockMsgClass,
    areAllVariantOOS = false,
    addToCartRef,
    expressPayConfig,
    isCurrentProductOptionLeavingSoon,
    disableAddToCart,
    showNewPdpDesignV2,
    isQuickATCModal,
    isFromCartPopup,
    handleShopInApp,
    enableExpressPayAnimation,
    hideKlarna,
    purchaseOption,
    selectedPlanId,
    showPurchaseTypeSelector,
  } = props

  const { t } = useTranslation('common')
  const { isB2BMode } = useStorefrontModeContext()
  const showATB = useATCVsATBExperiment()
  const atcLabel = showATB ? ProductCTAConstants.ADD_TO_BAG : ProductCTAConstants.ADD_TO_CART
  const selectedVariantId = selectedVariantPurchaseInfo?.selectedVariantId
  const isNonPhysicalProduct = product
    ? productHelper.getIsTruthyCustomField(product, CUSTOM_FIELD_NAMES.IS_NON_PHYSICAL_PRODUCT)
    : false

  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const { isBisWaitlistSmsEnabled, showSmsModal, setShowSmsModal, openSmsModalIfAllowed, onSubmitPhone } =
    useWaitlistSmsModal({ product, selectedOptions: props.selectedOptions })
  const { locale } = useLocaleContext()
  const eddExp = useStatsigExperiment(STATSIG_EXPERIMENTS.EDD)
  const disableForceEDD = useStatsigDynamicConfig<boolean>(DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS)
  const isEDDExpEnabled =
    // force edd for age restricted products as per existing logic
    eddExp.experimentBucket !== STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.EDD].buckets?.control ||
    (product && productHelper.getIsAgeRestrictedProduct(product) && !disableForceEDD) // force edd for age restricted products

  const { isGoldBarError } = useGoldBarProductEligibility(product, setErrorMessage)

  const { isBuyNowInProgress } = useCart()

  // Instant loading feedback for INP: the spinner paints within the click, then the heavy
  // onAddToCart runs after a yield (see useResponsiveAction). `isAdding` also reflects the shared
  // isAddToCartInProgress flag, so it drives all loaders directly.
  const { isPending: isAdding, run: runAddToCart } = useResponsiveAction(props.onAddToCart, {
    externalPending: props.isAddToCartInProgress,
  })

  const isExpressPayEnabled = useIsExpressPayEnabled()
  const showExpressPay = expressPayConfig && !expressPayConfig.disabled && isExpressPayEnabled
  const [isApplePayReady, setIsApplePayReady] = useState(false)

  const handleApplePayReady = useCallback(() => {
    setIsApplePayReady(true)
  }, [])

  // Common base conditions for messaging
  const baseMessagingConditions = getBaseMessagingConditions(
    selectedVariantPurchaseInfo,
    showVariantAvailableInventory,
    product,
    areAllVariantOOS,
    isGoldBarError,
    eddExp.init,
  )
  // Check if holiday message should be shown
  const holidayConfig = useStatsigDynamicHolidayConfig()
  const hasDisplayTags = hasValidDisplayTags(selectedVariantPurchaseInfo?.eddDetail?.displayTags, holidayConfig)

  let isHolidayMessageShown = false
  if (baseMessagingConditions && hasDisplayTags && selectedVariantPurchaseInfo?.eddDetail?.displayTags) {
    isHolidayMessageShown = checkHolidayMessageShown(
      selectedVariantPurchaseInfo.eddDetail.displayTags,
      holidayConfig,
      locale,
    )
  }

  const normalFlowShowsInStockMessage = baseMessagingConditions && !isEDDExpEnabled && !isHolidayMessageShown
  const showInStockAndReadyToShip = props.isPostOrderFlow ? true : normalFlowShowsInStockMessage

  const EMAIL_ERROR_MSG = t('please_enter_valid_email_address')
  const ctaText = t('preorder')

  // Reset invalid email address message when input element is absent.
  const revertEmailErrorMsg = () => {
    if (errorMessage === EMAIL_ERROR_MSG) {
      setErrorMessage('')
    }
  }

  const { ref: trackIsVisiblePreorderRef } = useTrackIsElementVisible(
    useCallback(() => getTrackingObject('preorder_button', selectedVariantId), [selectedVariantId]),
  )
  const { ref: trackIsVisibleWaitlistRef } = useTrackIsElementVisible(
    useCallback(() => getTrackingObject('waitlist_button', selectedVariantId), [selectedVariantId]),
  )

  const handleAddToCart = (isPreorder = false, selectedPlanId: number | undefined = undefined) => {
    runAddToCart(isPreorder, selectedPlanId)
  }

  const handleClick = () => {
    switch (type) {
      case CTA_TYPE.PREORDER:
        handleAddToCart(true)
        break

      case CTA_TYPE.WAITLIST:
        if (isCurrentProductOptionLeavingSoon) {
          props.onClickedShopSimilar?.()
          const element = document.querySelector(`[data-section-name="${SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF}"]`)
          if (element) {
            element.scrollIntoView({
              // @i18n-ignore
              behavior: 'smooth',
            })
          }
          return
        }
        if (selectedVariantPurchaseInfo?.hasJoinedWaitList) {
          return
        }
        if (AuthUtil.isLoggedIn()) {
          const cookieEmail =
            getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ??
            getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL) ??
            ''
          onJoinWaitList?.(cookieEmail)
          openSmsModalIfAllowed(cookieEmail)
        } else {
          if (validateEmail(email)) {
            onJoinWaitList?.(email)
            setErrorMessage('')
            setEmail('')
            openSmsModalIfAllowed(email)
          } else {
            setErrorMessage(EMAIL_ERROR_MSG)
          }
        }
        break

      case CTA_TYPE.ADD_TO_CART:
        props.isPostOrderFlow && props.handlePostOrderCallback
          ? props.handlePostOrderCallback()
          : handleAddToCart(
              false,
              showPurchaseTypeSelector && purchaseOption === PURCHASE_TYPES.SUBSCRIBE && selectedPlanId
                ? selectedPlanId
                : undefined,
            )
        break

      case CTA_TYPE.SHOP_IN_THE_APP:
        handleShopInApp?.({ product: props.product, selectedOptions: props.selectedOptions ?? [], isButtonClick: true })
        break

      default:
        console.warn('Invalid type')
    }
  }

  const getClassNameForQuickATCWrapper = () => {
    const { quickATCWrapper, quickATCWrapper_cartPopupSticky, quickATCWrapper_newDesignV2 } = styles as Record<
      string,
      string
    >

    const cartPopupStickyClassName = props.isFromCartPopup ? quickATCWrapper_cartPopupSticky : undefined
    const newDesignV2ClassName = showNewPdpDesignV2 ? quickATCWrapper_newDesignV2 : undefined

    return classNames(quickATCWrapper, cartPopupStickyClassName, newDesignV2ClassName)
  }
  const isDefaultCTAType =
    type !== CTA_TYPE.PREORDER &&
    type !== CTA_TYPE.WAITLIST &&
    type !== CTA_TYPE.OUT_OF_STOCK &&
    type !== CTA_TYPE.SHOP_IN_THE_APP
  const shouldKeepDefaultDesktopExpressPayRowMounted =
    showExpressPay && !props.isQuickATCForMobile && !props.isPostOrderFlow
  const shouldRenderDefaultCTA = isDefaultCTAType || shouldKeepDefaultDesktopExpressPayRowMounted

  let nonDefaultCTAContent: ReactNode = null
  switch (type) {
    case CTA_TYPE.PREORDER: {
      if (props.isPostOrderFlow) {
        nonDefaultCTAContent = (
          <ProductDisplayButton
            onClick={() => undefined}
            disabled={true}
            className={`width--full pointer-events-none`}
            showNewPdpDesignV2={showNewPdpDesignV2}
            product={props.product}
            selectedOptions={props.selectedOptions}
            setIsCartPopUpOpen={props.setIsCartPopUpOpen}
            isBuyNowPdp={props.isBuyNowPdp}
            isFromCartPopup={isFromCartPopup}
          >
            {t('out_of_stock')}
          </ProductDisplayButton>
        )
        break
      }
      revertEmailErrorMsg()
      nonDefaultCTAContent = (
        <div
          data-testid={TEST_LOCATORS.PRODUCT_CTA}
          ref={trackIsVisiblePreorderRef}
          className={classNames(styles.buttonWrapper, hideKlarna ? productFormStyles.productCTA_hideKlarna : undefined)}
        >
          {errorMessage && (
            <RenderErrorMessage errorMessage={errorMessage} isQuickATCForMobile={props.isQuickATCForMobile} />
          )}

          {props.isQuickATCForMobile ? (
            !props.isKeyboardVisibleForQuickATCForMobile && (
              // Only show the sticky button when keyboard is not visible
              <div className={getClassNameForQuickATCWrapper()}>
                <ProductDisplayButton
                  onClick={handleClick}
                  showLoader={isAdding}
                  selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
                  showNewPdpDesignV2={showNewPdpDesignV2}
                  product={props.product}
                  selectedOptions={props.selectedOptions}
                  setIsCartPopUpOpen={props.setIsCartPopUpOpen}
                  isBuyNowPdp={props.isBuyNowPdp}
                  isFromCartPopup={isFromCartPopup}
                >
                  {ctaText}
                </ProductDisplayButton>
              </div>
            )
          ) : (
            // Regular button for non-quickATC mode
            <ProductDisplayButton
              onClick={handleClick}
              showLoader={isAdding}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              showNewPdpDesignV2={showNewPdpDesignV2}
              product={props.product}
              selectedOptions={props.selectedOptions}
              setIsCartPopUpOpen={props.setIsCartPopUpOpen}
              isBuyNowPdp={props.isBuyNowPdp}
              isFromCartPopup={isFromCartPopup}
            >
              {ctaText}
            </ProductDisplayButton>
          )}
        </div>
      )
      break
    }

    case CTA_TYPE.WAITLIST: {
      nonDefaultCTAContent = (
        <ProductWaitlistCTA
          handleClick={handleClick}
          errorMessage={errorMessage}
          isQuickATCForMobile={props.isQuickATCForMobile}
          isBuyNowInProgress={isBuyNowInProgress}
          isAddToCartInProgress={props.isAddToCartInProgress}
          addToCartRef={addToCartRef}
          isKeyboardVisibleForQuickATCForMobile={props.isKeyboardVisibleForQuickATCForMobile}
          trackIsVisibleWaitlistRef={trackIsVisibleWaitlistRef}
          email={email}
          setEmail={setEmail}
          setIsKeyboardVisibleForQuickATCForMobile={props.setIsKeyboardVisibleForQuickATCForMobile}
          isPostOrderFlow={props.isPostOrderFlow}
          isCurrentProductOptionLeavingSoon={isCurrentProductOptionLeavingSoon}
          selectedVariantPurchaseInfo={selectedVariantPurchaseInfo}
          isQuickATCModal={isQuickATCModal}
          showNewPdpDesignV2={showNewPdpDesignV2}
          isFromCartPopup={isFromCartPopup}
        />
      )
      break
    }

    case CTA_TYPE.OUT_OF_STOCK: {
      nonDefaultCTAContent = (
        <>
          <RenderErrorMessage errorMessage={t('out_of_stock')} />
          <ProductDisplayButton
            onClick={() => undefined}
            disabled={true}
            className={`width--full pointer-events-none`}
            showNewPdpDesignV2={showNewPdpDesignV2}
            product={props.product}
            selectedOptions={props.selectedOptions}
            setIsCartPopUpOpen={props.setIsCartPopUpOpen}
            isBuyNowPdp={props.isBuyNowPdp}
            isFromCartPopup={isFromCartPopup}
          >
            {t(atcLabel)}
          </ProductDisplayButton>
        </>
      )
      break
    }

    case CTA_TYPE.SHOP_IN_THE_APP: {
      nonDefaultCTAContent = (
        <>
          {isNonPhysicalProduct && (
            <NonPhysicalProductDeliveryText
              inStockMsgClass={inStockMsgClass}
              isQuickATCForMobile={props.isQuickATCForMobile}
              isFromCartPopup={props.isFromCartPopup}
            />
          )}
          {showInStockAndReadyToShip ? (
            <InStockAndReadyToShip
              inStockMsgClass={inStockMsgClass}
              isQuickATCForMobile={props.isQuickATCForMobile}
              showNewPdpDesignV2={showNewPdpDesignV2}
              isFromCartPopup={isFromCartPopup}
            />
          ) : null}
          {selectedVariantPurchaseInfo?.showWaitList ? (
            <RenderErrorMessage
              className={showNewPdpDesignV2 ? styles.errorMessage_newDesignV2 : ''}
              errorMessage={t('out_of_stock')}
              showNewPdpDesignV2={showNewPdpDesignV2}
            />
          ) : null}
          <ProductDisplayButton
            onClick={handleClick}
            buttonType={BUTTON_TYPES.SECONDARY}
            showNewPdpDesignV2={showNewPdpDesignV2}
            product={props.product}
            selectedOptions={props.selectedOptions}
            setIsCartPopUpOpen={props.setIsCartPopUpOpen}
            isBuyNowPdp={props.isBuyNowPdp}
            isFromCartPopup={isFromCartPopup}
          >
            {t('shop_in_the_app')}
          </ProductDisplayButton>
        </>
      )
      break
    }

    default:
      revertEmailErrorMsg()
      break
  }

  const renderDefaultCTA = (isVisible: boolean) => (
    <div
      data-testid={isVisible ? TEST_LOCATORS.PRODUCT_CTA : undefined}
      className={classNames(
        !isVisible && styles.defaultCTAHidden,
        hideKlarna ? productFormStyles.productCTA_hideKlarna : undefined,
      )}
      aria-hidden={!isVisible}
    >
      {isVisible && isNonPhysicalProduct && (
        <NonPhysicalProductDeliveryText
          inStockMsgClass={inStockMsgClass}
          isQuickATCForMobile={props.isQuickATCForMobile}
          isFromCartPopup={props.isFromCartPopup}
        />
      )}
      {isVisible && showInStockAndReadyToShip ? (
        <InStockAndReadyToShip
          inStockMsgClass={inStockMsgClass}
          isQuickATCForMobile={props.isQuickATCForMobile}
          showNewPdpDesignV2={showNewPdpDesignV2}
          isFromCartPopup={isFromCartPopup}
        />
      ) : null}
      {isVisible && errorMessage && (
        <RenderErrorMessage
          errorMessage={errorMessage}
          isQuickATCForMobile={props.isQuickATCForMobile}
          showNewPdpDesignV2={showNewPdpDesignV2}
          hideIcon={isGoldBarError}
          isBuyNowPdp={props.isBuyNowPdp}
          isFromCartPopup={isFromCartPopup}
        />
      )}

      {isVisible && props.isQuickATCForMobile ? (
        !props.isKeyboardVisibleForQuickATCForMobile && (
          // Only show the sticky button when keyboard is not visible
          <div className={getClassNameForQuickATCWrapper()}>
            <ProductDisplayButton
              onClick={handleClick}
              showLoader={isAdding}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              buttonRef={addToCartRef}
              disabled={isGoldBarError || props.disableAddToCart}
              className={`width--full ${isBuyNowInProgress ? 'pointer-events-none' : ''}`}
              showNewPdpDesignV2={showNewPdpDesignV2}
              product={props.product}
              selectedOptions={props.selectedOptions}
              setIsCartPopUpOpen={props.setIsCartPopUpOpen}
              isBuyNowPdp={props.isBuyNowPdp}
              isFromCartPopup={isFromCartPopup}
            >
              {t(atcLabel)}
            </ProductDisplayButton>
          </div>
        )
      ) : isVisible && props.isPostOrderFlow ? (
        <div className={styles.postOrderCtaWrapper}>
          <ProductDisplayButton
            onClick={handleClick}
            showLoader={isAdding}
            selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
            buttonRef={addToCartRef}
            disabled={isGoldBarError || disableAddToCart}
            className={`width--full ${isBuyNowInProgress ? 'pointer-events-none' : ''}`}
            showNewPdpDesignV2={showNewPdpDesignV2}
            product={props.product}
            selectedOptions={props.selectedOptions}
            setIsCartPopUpOpen={props.setIsCartPopUpOpen}
            isBuyNowPdp={props.isBuyNowPdp}
            isFromCartPopup={isFromCartPopup}
          >
            {t('continue_with_this_item')}
          </ProductDisplayButton>
        </div>
      ) : !props.isQuickATCForMobile &&
        !props.isPostOrderFlow &&
        (isVisible || shouldKeepDefaultDesktopExpressPayRowMounted) ? (
        <div
          className={classNames(
            showExpressPay && enableExpressPayAnimation && styles.defaultAtcWrapperForApplePay,
            showExpressPay && enableExpressPayAnimation && isApplePayReady && styles.defaultAtcWrapperForApplePay_ready,
            showExpressPay && !enableExpressPayAnimation && styles.expressPayWrapperNoAnimation,
            showExpressPay && showNewPdpDesignV2 && styles.expressPayWrapper_newDesignV2,
          )}
        >
          <ProductDisplayButton
            onClick={handleClick}
            showLoader={isAdding}
            selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
            buttonRef={addToCartRef}
            disabled={isGoldBarError || disableAddToCart}
            className={classNames(
              'width--full',
              isBuyNowInProgress && 'pointer-events-none',
              showExpressPay && enableExpressPayAnimation && styles.atcButtonWithExpressPay,
              showExpressPay && enableExpressPayAnimation && isApplePayReady && styles.atcButtonWithExpressPay_ready,
              showExpressPay && !enableExpressPayAnimation && styles.atcButtonHalfWidth,
            )}
            buttonType={isB2BMode ? BUTTON_TYPES.SECONDARY : undefined}
            showNewPdpDesignV2={showNewPdpDesignV2}
            product={props.product}
            selectedOptions={props.selectedOptions}
            setIsCartPopUpOpen={props.setIsCartPopUpOpen}
            isBuyNowPdp={props.isBuyNowPdp}
            isFromCartPopup={isFromCartPopup}
          >
            {t(atcLabel)}
          </ProductDisplayButton>

          {showExpressPay && (
            <ExpressPay
              config={expressPayConfig}
              dataSelectorKey={QUINCE_BOT_LOCATORS.PDP.APPLE_PAY_CLICK}
              className={classNames(
                enableExpressPayAnimation && styles.displayButtonWrapper,
                !enableExpressPayAnimation && styles.applePayButtonHalfWidth,
                (isGoldBarError || disableAddToCart) && styles.applePayCTADisabled,
                isBuyNowInProgress && 'pointer-events-none',
              )}
              productPrice={
                showPurchaseTypeSelector && purchaseOption === PURCHASE_TYPES.SUBSCRIBE && props.subscriptionPrice
                  ? props.subscriptionPrice
                  : props.salePrice
              }
              onReady={handleApplePayReady}
              enableAnimation={enableExpressPayAnimation}
              disableClick={isGoldBarError || disableAddToCart}
            />
          )}
        </div>
      ) : null}
    </div>
  )

  return (
    <>
      {nonDefaultCTAContent}
      {shouldRenderDefaultCTA ? renderDefaultCTA(isDefaultCTAType) : null}
      {isBisWaitlistSmsEnabled ? (
        <WaitlistSmsModal
          open={showSmsModal}
          onClose={() => {
            setShowSmsModal(false)
          }}
          onSubmitPhone={onSubmitPhone}
        />
      ) : null}
    </>
  )
}

const ProductCTAWrapper = (props: ProductCTAType) => {
  const { isGoldBarError } = useGoldBarProductEligibility(props.product)
  const { locale } = useLocaleContext()
  const holidayConfig = useStatsigDynamicHolidayConfig()
  const eddExp = useStatsigExperiment(STATSIG_EXPERIMENTS.EDD)
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE

  // Common base conditions for messaging
  const baseMessagingConditions = getBaseMessagingConditions(
    props.selectedVariantPurchaseInfo,
    props.showVariantAvailableInventory,
    props.product,
    props.areAllVariantOOS ?? false,
    isGoldBarError,
    eddExp.init,
  )

  // Check if holiday message should be shown (independent conditions) similiar to instock readytoship logic
  const hasDisplayTags = hasValidDisplayTags(props.selectedVariantPurchaseInfo?.eddDetail?.displayTags, holidayConfig)

  let showHolidayMessage = false
  if (baseMessagingConditions && hasDisplayTags && props.selectedVariantPurchaseInfo?.eddDetail?.displayTags) {
    showHolidayMessage = checkHolidayMessageShown(
      props.selectedVariantPurchaseInfo.eddDetail.displayTags,
      holidayConfig,
      locale,
    )
  }

  const showEDDMsg = productHelper.getIsAgeRestrictedProduct(props.product as Product)
    ? props.type === CTA_TYPE.ADD_TO_CART
    : !(
        props.type === CTA_TYPE.WAITLIST ||
        // suggestion is wrong by eslint
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        props.selectedVariantPurchaseInfo?.isPreorder ||
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        props.selectedVariantPurchaseInfo?.showMadeToOrder ||
        getIsGiftCard(props.product?.id) ||
        props.product?.isMadeToOrder
      )

  return (
    <>
      {showHolidayMessage && !props.isPostOrderFlow ? (
        <HolidayMessage
          inStockMsgClass={props.inStockMsgClass}
          isQuickATCForMobile={props.isQuickATCForMobile}
          showNewPdpDesignV2={props.showNewPdpDesignV2}
          eddDetail={props.selectedVariantPurchaseInfo?.eddDetail}
        />
      ) : null}
      {showEDDMsg && props.product && !isGoldBarError && !props.isPostOrderFlow ? (
        <EDDMessageContainer
          eddDetail={props.selectedVariantPurchaseInfo?.eddDetail}
          product={props.product}
          selectedInternalVariantId={props.selectedVariantPurchaseInfo?.selectedInternalVariantId}
          isQuickATCForMobile={props.isQuickATCForMobile}
          showNewPdpDesignV2={props.showNewPdpDesignV2}
          showIcon={isMobile}
          holidayMessageShown={showHolidayMessage}
        />
      ) : null}
      <ProductCTA {...props} />
      <div ref={props.atcSectionEndRef} />
    </>
  )
}
export default ProductCTAWrapper
